import {
  PListDto,
  PListDetailsDto,
  OrderSSCCDto,
  CreateOrderSSCCRequest,
  PalletSummaryDto,
  OrderSerialNoDto,
  CreateOrderSerialNoRequest,
  OrderBatchNoDto,
  CreateOrderBatchNoRequest,
  UpdateOrderBatchNoRequest
} from '../types/picking';
import { CompletedPickListDto, CompletedPickListDetailDto, CompletedPickListLineDto } from '../types/completedPickingList';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class PickingService {

  async getPickingLists(operatorId?: string): Promise<PListDto[]> {
    const url = `${API_BASE_URL}/picking/lists${operatorId ? `?operatorId=${operatorId}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  // OrderSSCC (Pallet Management) Methods
  async getPallets(pListId: number): Promise<PalletSummaryDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/pallets`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch pallets: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getOrderSSCCs(pListId: number): Promise<OrderSSCCDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch SSCC records: ${response.statusText}`);
    }
    
    return response.json();
  }

  async createOrUpdateSSCC(pListId: number, request: CreateOrderSSCCRequest): Promise<OrderSSCCDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc`;
    const response = await AuthService.authenticatedPost(url, request);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create/update SSCC: ${response.statusText}`);
    }
    
    return response.json();
  }

  async deleteSSCC(pListId: number, ssccId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/sscc/${ssccId}`;
    const response = await AuthService.authenticatedDelete(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to delete SSCC: ${response.statusText}`);
    }
  }

  async getSSCCContents(sscc: string): Promise<OrderSSCCDto[]> {
    const url = `${API_BASE_URL}/picking/sscc/${encodeURIComponent(sscc)}`;
    const response = await AuthService.authenticatedGet(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch SSCC contents: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPickingDetails(pListId: number): Promise<PListDetailsDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking details: ${response.statusText}`);
    }

    return response.json();
  }

  async startPicking(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/start`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to start picking: ${response.statusText}`);
    }
  }

  async completePicking(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/complete`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to complete picking: ${response.statusText}`);
    }
  }

  async sendToPacking(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/to-packing`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to send to packing: ${response.statusText}`);
    }
  }

  async updatePickedQuantity(
    pListId: number,
    lineNo: number,
    qtyPicked: number,
    operatorId?: string,
    startDateTime?: string
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines`;
    const response = await AuthService.authenticatedPut(url, {
      lineNo,
      qtyPicked,
      operatorId,
      startDateTime
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update picked quantity: ${response.statusText}`);
    }
  }

  async updateBinCode(
    pListId: number,
    lineNo: number,
    binCode: string,
    operatorId?: string
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}/bincode`;
    const response = await AuthService.authenticatedPut(url, {
      binCode,
      operatorId
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update BinCode: ${response.statusText}`);
    }
  }


  async getAvailablePickingLists(operatorId?: string): Promise<PListDto[]> {
    const url = `${API_BASE_URL}/picking/available${operatorId ? `?operatorId=${operatorId}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch available picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  async lockPickingList(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lock`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to lock picking list: ${response.statusText}`);
    }
  }

  async unlockPickingList(pListId: number, operatorId?: string): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/unlock`;
    const response = await AuthService.authenticatedPost(url, operatorId ? { operatorId } : undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to unlock picking list: ${response.statusText}`);
    }
  }

  async getPickingListsByStatus(status: number): Promise<PListDto[]> {
    const url = `${API_BASE_URL}/picking/lists/by-status?status=${status}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking lists by status: ${response.statusText}`);
    }

    return response.json();
  }

  async getPickingListsByStatuses(statuses: number[]): Promise<PListDto[]> {
    const statusParams = statuses.map(s => `statuses=${s}`).join('&');
    const url = `${API_BASE_URL}/picking/lists/by-statuses?${statusParams}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking lists by statuses: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get picking list history (status 60 - completed)
   * @param operatorId Optional operator ID filter. If null, filters to current operator
   * @param dateFrom Optional start date filter
   * @param dateTo Optional end date filter
   * @param allOperators If true, returns history for all operators (for warehouse managers)
   */
  async getPickingListHistory(
    operatorId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    allOperators: boolean = false
  ): Promise<PListDto[]> {
    const params = new URLSearchParams();

    if (operatorId) {
      params.append('operatorId', operatorId);
    }
    if (dateFrom) {
      params.append('dateFrom', dateFrom.toISOString());
    }
    if (dateTo) {
      params.append('dateTo', dateTo.toISOString());
    }
    if (allOperators) {
      params.append('allOperators', 'true');
    }

    const url = `${API_BASE_URL}/picking/history${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch picking list history: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Format date for display
   */
  formatDate(dateString?: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO');
  }

  /**
   * Format date and time for display
   */
  formatDateTime(dateString?: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('no-NO');
  }

  /**
   * Get picking list status text
   */
  getStatusText(status: number): string {
    switch (status) {
      case 0: return 'Ubehandlet';
      case 10: return 'Godkjent';
      case 20: return 'Under arbeid';
      case 30: return 'Behandlet ikke komplett';
      case 40: return 'Behandlet komplett';
      case 45: return 'Til pakking';
      case 50: return 'Klar for eksport';
      case 60: return 'Eksportert';
      default: return `Status ${status}`;
    }
  }

  /**
   * Update a picking list line
   */
  async updatePListLine(pListId: number, lineNo: number, line: any): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${lineNo}`;
    const response = await AuthService.authenticatedPut(url, line);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update picking list line: ${response.statusText}`);
    }
  }

  /**
   * Update the sort order of picking list lines
   */
  async updateLineOrder(
    pListId: number,
    lineOrders: Array<{ pListLineNo: number; sortOrder: number }>
  ): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/reorder`;
    const response = await AuthService.authenticatedPut(url, lineOrders);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update line order: ${response.statusText}`);
    }
  }

  /**
   * Update a picking list header (PList)
   */
  async updatePickingList(pListId: number, pickingList: any): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}`;
    const response = await AuthService.authenticatedPut(url, pickingList);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update picking list: ${response.statusText}`);
    }
  }

  // ===== New methods for Completed Picking Lists (Avsluttede plukklister) =====

  /**
   * Get completed pick lists (status 60) with enhanced information.
   * Includes calculated delivery dates and aggregated signature flags.
   * Default date range: last 7 days.
   *
   * @param dateFrom Optional start date filter (defaults to 7 days ago)
   * @param dateTo Optional end date filter (defaults to today)
   * @returns List of completed pick lists with enhanced information
   */
  async getCompletedPickLists(
    dateFrom?: Date,
    dateTo?: Date
  ): Promise<CompletedPickListDto[]> {
    // Default to last 7 days if no date range provided
    const defaultDateFrom = new Date();
    defaultDateFrom.setDate(defaultDateFrom.getDate() - 7);

    const fromDate = dateFrom || defaultDateFrom;
    const toDate = dateTo || new Date();

    const params = new URLSearchParams();
    params.append('dateFrom', fromDate.toISOString());
    params.append('dateTo', toDate.toISOString());

    const url = `${API_BASE_URL}/picking/completed?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get complete details for a single completed pick list.
   * Includes header, order lines, and package information in a single request.
   *
   * @param pListId Pick list ID
   * @returns Complete pick list details or null if not found
   */
  async getCompletedPickListDetail(pListId: number): Promise<CompletedPickListDetailDto | null> {
    const url = `${API_BASE_URL}/picking/completed/${pListId}`;
    const response = await AuthService.authenticatedGet(url);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick list detail: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get order lines for a completed pick list.
   * Includes picking details from PListLine table.
   *
   * @param pListId Pick list ID
   * @returns List of order lines with picking details
   */
  async getCompletedPickListLines(pListId: number): Promise<CompletedPickListLineDto[]> {
    const url = `${API_BASE_URL}/picking/completed/${pListId}/lines`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch completed pick list lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Batch approve picking lists by order numbers
   * @param orderNumbers Array of order numbers to approve
   * @param targetStatus Optional target status (defaults to 10)
   * @param operatorId Optional operator ID
   * @returns Batch approval result with success/failure details
   */
  async batchApprovePListsByOrders(
    orderNumbers: number[],
    targetStatus?: number,
    operatorId?: string
  ): Promise<BatchApprovalResult> {
    const url = `${API_BASE_URL}/picking/lists/batch-approve`;
    const response = await AuthService.authenticatedPost(url, {
      orderNumbers,
      targetStatus,
      operatorId
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to batch approve pick lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get packages for a batch picking list (PListIsBatchYesNo = true).
   * Used to display packages to operator before starting batch pick.
   * @param pListId The picking list ID
   * @returns Array of packages with packaging type description
   */
  async getPackagesForPList(pListId: number): Promise<PackageDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/packages`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch packages: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== Serial Number Registration Methods =====

  /**
   * Get serial numbers for a specific picking line
   */
  async getSerialNumbers(pListId: number, orderNo: number, lineNo: number): Promise<OrderSerialNoDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch serial numbers: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Register a new serial number for a picking line
   */
  async createSerialNumber(pListId: number, orderNo: number, lineNo: number, serialNo: string): Promise<OrderSerialNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`;
    const request: CreateOrderSerialNoRequest = {
      pListID: pListId,
      orderNo,
      lineNo,
      serialNo
    };

    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create serial number: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a serial number registration
   */
  async deleteSerialNumber(pListId: number, serialNoId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/serialnumbers/${serialNoId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to delete serial number: ${response.statusText}`);
    }
  }

  // ===== Batch Number Registration Methods =====

  /**
   * Get batch numbers for a specific picking line
   */
  async getBatchNumbers(pListId: number, orderNo: number, lineNo: number): Promise<OrderBatchNoDto[]> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/batchnumbers`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch batch numbers: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Register a new batch number for a picking line
   */
  async createBatchNumber(
    pListId: number,
    orderNo: number,
    lineNo: number,
    pListLineNo: number,
    batchNo: string,
    quantity: number,
    quantityGross?: number
  ): Promise<OrderBatchNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/batchnumbers`;
    const request: CreateOrderBatchNoRequest = {
      pListID: pListId,
      orderNo,
      lineNo,
      pListLineNo,
      batchNo,
      quantity,
      quantityGross
    };

    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create batch number: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a batch number registration
   */
  async updateBatchNumber(pListId: number, batchNoId: number, quantity: number, quantityGross?: number): Promise<OrderBatchNoDto> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/batchnumbers/${batchNoId}`;
    const request: UpdateOrderBatchNoRequest = {
      quantity,
      quantityGross
    };

    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update batch number: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a batch number registration
   */
  async deleteBatchNumber(pListId: number, batchNoId: number): Promise<void> {
    const url = `${API_BASE_URL}/picking/lists/${pListId}/batchnumbers/${batchNoId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to delete batch number: ${response.statusText}`);
    }
  }
}

// Package types for batch picking
export interface PackageDto {
  packageNo: number;
  visiblePackageNo?: number;
  packagingTypeNo: number;
  packagingTypeDescription?: string;
  pListID: number;
  description1?: string;
  description2?: string;
  carrierName?: string;
  weight?: number;
  volume?: number;
}

// Batch approval types
export interface BatchApprovalRequest {
  orderNumbers: number[];
  targetStatus?: number;
  operatorId?: string;
}

export interface BatchApprovalResult {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  notFoundCount: number;
  skippedCount: number;
  results: BatchApprovalItemResult[];
  errors: string[];
}

export interface BatchApprovalItemResult {
  pListID: number;
  orderNo: number;
  success: boolean;
  errorMessage?: string;
  oldStatus?: number;
  newStatus?: number;
}

export const pickingService = new PickingService();
